/**
 * KanCraft's #shop-life Slack channel in the station strip (since 2026-10-29).
 *
 * jobq-api "SHOP LIFE FEED" (shop_life.py) reads the channel with the JobQ Slack bot and answers
 * GET /shop_life/feed: posts newest first, each one's text already split into segments
 * ({t: text|bold|italic|strike|code|mention|link, v, href?}) so nothing here renders HTML.
 * Photos come through GET /shop_life/files/{id}/thumb (Slack refuses them without the bot token);
 * they are fetched with fetch() so config.js's wrapper adds the API key, and kept as blob URLs.
 */
import config from '../../config.js';
import { createBlobCache } from '../../utils/cameraRelay';

export const FEED_POLL_MS = 60000;
export const FEED_LIMIT = 15;
export const NEW_POST_MS = 30 * 60 * 1000;

const apiBase = () => config.API_BASE_URL;

/** {ok: true, data} or {ok: false, message}. Never throws. */
export async function loadShopLifeFeed(limit = FEED_LIMIT) {
  try {
    const res = await fetch(`${apiBase()}/shop_life/feed?limit=${limit}`, { cache: 'no-store' });
    if (!res.ok) {
      let message = 'Shop Life is unavailable right now.';
      try {
        const body = await res.json();
        if (body && typeof body.detail === 'string') message = body.detail;
      } catch (_) { /* keep the default */ }
      return { ok: false, message };
    }
    return { ok: true, data: await res.json() };
  } catch (_) {
    return { ok: false, message: 'Shop Life is unavailable right now.' };
  }
}

export function photoUrl(fileId, size = 'small') {
  return `${apiBase()}/shop_life/files/${encodeURIComponent(fileId)}/thumb?size=${size === 'large' ? 'large' : 'small'}`;
}

// fileId:size -> blob URL, the oldest revoked past PHOTO_CACHE_MAX (a kiosk tab stays open for days).
// A failed load is not kept, so the next poll tries again.
export const PHOTO_CACHE_MAX = 80;
const photos = createBlobCache(PHOTO_CACHE_MAX);
const loading = new Map();

export function loadPhoto(fileId, size = 'small') {
  const key = `${fileId}:${size}`;
  const hit = photos.get(key);
  if (hit) return Promise.resolve(hit);
  if (!loading.has(key)) {
    loading.set(key, fetch(photoUrl(fileId, size))
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => (blob ? URL.createObjectURL(blob) : null))
      .catch(() => null)
      .then((url) => {
        loading.delete(key);
        if (url) photos.set(key, url);
        return url;
      }));
  }
  return loading.get(key);
}

export function __resetShopLifePhotosForTests() {
  photos.clear();
  loading.clear();
}

/** "now", "5m", "3h", "Tue", "Oct 22" in the viewer's own time (the shop's PCs are on Central). */
export function timeAgo(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((now - t) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h`;
  if (mins < 6 * 24 * 60) return new Date(t).toLocaleDateString('en-US', { weekday: 'short' });
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fullTime(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function isNewPost(post, now = Date.now()) {
  const t = Date.parse(post && post.posted_at);
  return !Number.isNaN(t) && now - t < NEW_POST_MS;
}

/** The API already sends only http(s) / mailto links; checked again here before anything is clickable. */
export function safeHref(href) {
  return typeof href === 'string' && /^(https?:\/\/|mailto:)/i.test(href) ? href : null;
}

export function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((p) => p[0]).join('') || '?').toUpperCase();
}

export function plainText(segments) {
  return (segments || []).map((s) => s.v).join('');
}
